// Each rendered page's viewport transform (PDF user space → the page's CSS
// pixels), so a drag that ends on another page can be mapped into that
// page's user space (AC-XPAGE-1).
import { apply, invert, multiply, translate, type Matrix } from '../engine/content/matrix.ts';

const transforms = new Map<number, Matrix>();

export function setViewport(page: number, transform: number[]): void {
  transforms.set(page, transform as Matrix);
}

export function clearViewport(page: number, transform: number[]): void {
  if (transforms.get(page) === transform) transforms.delete(page);
}

/**
 * The user-space transform that moves an object from page `from` to page `to` so it lands `delta` CSS px from where
 * the drag started (in the other page's CSS px), looking as it did on screen: user A → CSS A, moved, CSS B → user B.
 */
export function crossPageTransform(from: number, to: number, delta: [number, number]): Matrix | undefined {
  const a = transforms.get(from);
  const b = transforms.get(to);
  if (!a || !b) return undefined;
  return multiply(multiply(a, translate(delta[0], delta[1])), invert(b));
}

/**
 * A turn on screen, as a user-space transform for page `page`: `degrees` clockwise as seen (CSS rotate) around the
 * page CSS point (cx, cy). Through the page's viewport, so it turns the way it looks on a page shown turned too.
 */
export function screenRotation(page: number, degrees: number, cx: number, cy: number): Matrix | undefined {
  const v = transforms.get(page);
  if (!v) return undefined;
  const a = (degrees * Math.PI) / 180;
  const turn: Matrix = multiply(multiply(translate(-cx, -cy), [Math.cos(a), Math.sin(a), -Math.sin(a), Math.cos(a), 0, 0]), translate(cx, cy));
  return multiply(multiply(v, turn), invert(v));
}

/**
 * The top-left corner as seen on screen of points in page `page`'s user space, in user space (so a later zoom does
 * not move it). Without the page's viewport: the smallest x and largest y (a page shown upright).
 */
export function topLeftUser(page: number, points: [number, number][]): [number, number] {
  const v = transforms.get(page);
  if (!v) return [Math.min(...points.map((p) => p[0])), Math.max(...points.map((p) => p[1]))];
  const css = points.map(([x, y]) => apply(v, x, y));
  return apply(invert(v), Math.min(...css.map((p) => p[0])), Math.min(...css.map((p) => p[1])));
}

/**
 * The transform that puts what was copied on page `from` with its top-left `anchor` (user space) under the pointer
 * at `pointer` (CSS px of page `to`), looking as it did on screen (AC-PASTE-1). Without the first page's viewport (not
 * drawn now), a plain move. Undefined when page `to` is not drawn.
 */
export function placeAt(from: number, anchor: [number, number], to: number, pointer: [number, number]): Matrix | undefined {
  const a = transforms.get(from);
  const b = transforms.get(to);
  if (!b) return undefined;
  if (!a) {
    const q = apply(invert(b), pointer[0], pointer[1]);
    return translate(q[0] - anchor[0], q[1] - anchor[1]);
  }
  const at = apply(a, anchor[0], anchor[1]);
  return crossPageTransform(from, to, [pointer[0] - at[0], pointer[1] - at[1]]);
}

/** Page `page`'s CSS point (x, y) in its user space; undefined when the page is not drawn. */
export function userAt(page: number, x: number, y: number): [number, number] | undefined {
  const v = transforms.get(page);
  return v && apply(invert(v), x, y);
}
